import { useEffect, useMemo, useRef, useState } from "react";

/**
 * CustomAudioPlayer
 * Styled to visually belong under the intonation comparison panel.
 */

export default function CustomAudioPlayer({
  audio,
  onTimeUpdate,
  title = "Your recording",
  helper = "Play your recording and compare the curve above.",
  showKeyboardHint = true,
  compact = true,
}) {
  const ref = useRef(null);

  const [isPlaying, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Reset when audio changes
  useEffect(() => {
    setPlaying(false);
    setCurrent(0);
    setDuration(0);
    if (ref.current) {
      ref.current.pause();
      ref.current.currentTime = 0;
      ref.current.playbackRate = rate;
    }
  }, [audio]); // eslint-disable-line

  const togglePlay = () => {
    const el = ref.current;
    if (!el || !audio) return;
    el.paused ? el.play() : el.pause();
  };

  const setSpeed = (r) => {
    setRate(r);
    if (ref.current) ref.current.playbackRate = r;
  };

  const fmt = (t) => {
    if (!Number.isFinite(t)) return "0:00";
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const progressPct = useMemo(() => {
    if (!duration || !Number.isFinite(duration)) return 0;
    return Math.max(0, Math.min(100, (current / duration) * 100));
  }, [current, duration]);

  return (
    <div className={compact ? "w-full" : "w-full max-w-md mx-auto"}>
      <div className="rounded-xl border border-white/50 bg-white/60 backdrop-blur-sm shadow-sm px-3 sm:px-4 py-3">

        {/* Hidden audio element */}
        <audio
          src={audio || undefined}
          ref={ref}
          preload="metadata"
          onLoadedMetadata={(e) => {
            const d = e.currentTarget.duration;
            setDuration(Number.isFinite(d) ? d : 0);
          }}
          onTimeUpdate={(e) => {
            const t = e.currentTarget.currentTime;
            setCurrent(t);
            onTimeUpdate?.(t);
          }}
          onEnded={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
        />

        {/* Top row */}
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-r from-emerald-500 to-green-500 text-white flex items-center justify-center shadow-sm shrink-0">
            🎙️
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <div className="font-semibold text-gray-900 truncate">
                {title}
              </div>
              <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200/60 px-2 py-0.5 rounded-full shrink-0">
                your audio
              </span>
            </div>

            <div className="text-xs text-gray-600 leading-snug">
              {helper}
              {showKeyboardHint && !isMobile && (
                <span className="ml-2 text-[11px] text-gray-500">
                  Shortcut: P
                </span>
              )}
            </div>
          </div>

          <div className="text-xs text-gray-600 tabular-nums shrink-0">
            {fmt(current)} <span className="text-gray-400">/</span>{" "}
            {fmt(duration)}
          </div>
        </div>

        {/* Progress */}
        <div className="mt-2">
          <div className="h-1.5 rounded-full bg-gray-200/70 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-500 to-green-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        {/* Controls */}
        <div
          className={[
            "mt-3 flex items-center gap-3",
            isMobile ? "flex-col items-stretch" : "flex-row",
          ].join(" ")}
        >
          {/* Play Button */}
          <button
            type="button"
            onClick={togglePlay}
            disabled={!audio}
            className={[
              "inline-flex items-center justify-center gap-2",
              "rounded-xl font-semibold transition-all border shadow-sm",
              audio
                ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-blue-700/20 hover:shadow-md"
                : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed",
              isMobile ? "w-full h-11" : "h-11 px-4",
            ].join(" ")}
          >
            <span className="text-lg leading-none">
              {isPlaying ? "⏸" : "▶️"}
            </span>
            <span className="text-sm">
              {isPlaying ? "Pause" : "Play"} my recording
            </span>
          </button>

          {/* Playback Speed (consistent with intonation panel) */}
          <div
            className={[
              "flex-1 flex items-center gap-3",
              isMobile ? "w-full justify-between" : "justify-end",
            ].join(" ")}
          >
            <span className="text-xs font-medium text-gray-500 whitespace-nowrap">
              Playback speed
            </span>

            <div className="inline-flex rounded-xl bg-white/80 border border-gray-200 shadow-sm p-1">
              {[0.25, 0.5, 1].map((speed) => {
                const active = rate === speed;
                return (
                  <button
                    key={speed}
                    type="button"
                    onClick={() => setSpeed(speed)}
                    className={[
                      "px-3 py-1.5 rounded-lg text-sm font-semibold transition-all",
                      active
                        ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow"
                        : "text-gray-600 hover:bg-white",
                    ].join(" ")}
                  >
                    {speed}x
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Micro tip */}
        <div className="mt-2 text-[11px] text-gray-500">
          Tip: listen for the{" "}
          <span className="font-semibold">rise/fall</span> on each word and
          match the narrator’s shape above.
        </div>
      </div>
    </div>
  );
}